import { HStack, Text, Show, Box } from '@chakra-ui/react';
import {
  CallMoreSmallIcon,
  CallSpeakerSmallIcon,
  VideoPoorNetIcon,
  VideoErrNetIcon,
  CallScreenMinIcon,
  CallZoomInIcon,
  CallPinIcon,
  CallFullScreenIcon,
} from '@halo/ui-chat-react';
import { useCyclicVisibility } from '@halo/ui-chat-react/hooks';
import { useTranslation } from 'react-i18next';
import { MyTrans } from '@halo/ui-react';
import CardMenu from './CardMenu';
import { CardMoreOperateKey } from './type';

export interface CallMainPanelFooterProps {
  disabled?: boolean;
  member?: Record<string, any>;
  cardRef?: React.RefObject<HTMLDivElement | null>;
  handleDrawSpectrum?: (...args: any) => void;
  onAudioInfo?: (info: { hasAudioStream: boolean; hasVolume: boolean }) => void;
  onFixed?: () => void;
}

export enum CallStatusIcon {
  SCREEN_SHARING = 'screenSharing',
  POOR_NET = 'poorNet',
  ERROR_NET = 'errorNet',
  MUTE = 'mute',
}

export function CallMainPanelFooter({
  disabled = true,
  member,
  cardRef,
  handleDrawSpectrum,
  onAudioInfo,
  onFixed,
}: CallMainPanelFooterProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const { show, checkShow } = useCyclicVisibility();

  const name = useMemo(() => {
    return member?.isSelf ? t('calls.me') : member?.name;
  }, [member?.isSelf, member?.name, t]);

  const netInfo = useMemo(() => {
    const info = { isPoorNet: false, isErrNet: false };
    if (member?.network?.ingress && member?.network?.egress) {
      info.isPoorNet = Math.floor((member?.network?.ingress + member?.network?.egress) / 2) <= 1;
    } else if (member?.network?.ingress) {
      info.isPoorNet = member?.network?.ingress <= 1;
    } else if (member?.network?.egress) {
      info.isPoorNet = member?.network?.egress <= 1;
    } else {
      info.isPoorNet = true;
      info.isErrNet = true;
    }
    return info;
  }, [member?.network?.egress, member?.network?.ingress]);

  const status = useMemo(() => {
    if (netInfo.isPoorNet && !member?.isSelf && show) {
      return netInfo.isErrNet ? CallStatusIcon.ERROR_NET : CallStatusIcon.POOR_NET;
    } else if (member?.screenSharing) {
      return CallStatusIcon.SCREEN_SHARING;
    } else {
      return CallStatusIcon.MUTE;
    }
  }, [netInfo, show, member?.isSelf, member?.screenSharing]);

  const Icon = useMemo(() => {
    switch (status) {
      case CallStatusIcon.SCREEN_SHARING:
        return <CallScreenMinIcon />;
      case CallStatusIcon.POOR_NET:
        return <VideoPoorNetIcon />;
      case CallStatusIcon.ERROR_NET:
        return <VideoErrNetIcon />;
      case CallStatusIcon.MUTE:
        return (
          <DrawSpeak
            handleDrawSpectrum={handleDrawSpectrum}
            member={member}
            onAudioInfo={onAudioInfo}
          />
        );
      default:
        return null;
    }
  }, [handleDrawSpectrum, member, status, onAudioInfo]);

  const handleClickMore = useCallback((key: string) => {
    switch (key) {
      case CardMoreOperateKey.MUTE:
        // setVolume(callUserInfo!.id, MediaType.MICROPHONECAMERA_AUDIO, 0);
        break;
      case CardMoreOperateKey.WATCH:
        break;
      case CardMoreOperateKey.PIN:
        onFixed?.();
        break;
      case CardMoreOperateKey.FULL_SCREEN:
        cardRef?.current?.requestFullscreen();
        break;
      default:
        break;
    }
  }, []);

  const items = useMemo(
    () => [
      {
        key: CardMoreOperateKey.PIN,
        label: (
          <>
            <MyTrans i18nKey="calls.pin" />
            <CallPinIcon />
          </>
        ),
      },
      {
        key: CardMoreOperateKey.FULL_SCREEN,
        label: (
          <>
            <MyTrans i18nKey="calls.fullScreen" /> <CallZoomInIcon />
          </>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    checkShow(!netInfo.isPoorNet);
  }, [checkShow, netInfo.isPoorNet]);

  return (
    <HStack alignItems="center" justifyContent="space-between" w="full" px="3" pb="3" ref={ref}>
      <HStack
        backdropFilter="blur(4px)"
        gap="1"
        px="2"
        py="1.5"
        color="white"
        bg="blackAlpha.20"
        rounded="lg"
      >
        {!disabled && Icon}
        <Text truncate maxW="120px" fontSize="sm" lineHeight="1.4">
          {name}
        </Text>
      </HStack>
      {(!member?.isSelf || member?.screenSharing) && (
        <>
          {member?.fixed ? (
            <CallFullScreenIcon
              cursor={'pointer'}
              onClick={() => cardRef?.current?.requestFullscreen()}
            />
          ) : (
            <CardMenu onClick={handleClickMore} data={items} container={cardRef}>
              <Box backdropFilter="blur(4px)" bg="blackAlpha.20" rounded="full">
                <CallMoreSmallIcon />
              </Box>
            </CardMenu>
          )}
        </>
      )}
    </HStack>
  );
}

export default CallMainPanelFooter;

export function DrawSpeak({
  handleDrawSpectrum,
  member,
  onAudioInfo,
}: {
  handleDrawSpectrum: any;
  member: any;
  onAudioInfo?: (info: { hasAudioStream: boolean; hasVolume: boolean }) => void;
}) {
  const [hasAudioStream, setHasAudioStream] = useState(false);
  const [hasVolume, setHasVolume] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current && member?.callActionMap?.microphoneCamera_audio) {
      const frameId = `${member.user_id}`;

      handleDrawSpectrum(
        member.user_id,
        canvasRef.current,
        frameId,
        () => {
          setHasAudioStream(false);
          return Promise.resolve(false);
        },
        () => {
          setHasAudioStream(true);
        },
        (data: Uint8Array<ArrayBuffer>) => {
          setHasVolume(!!data[0]);
        }
      );
    } else {
      setHasAudioStream(false);
    }
  }, [
    handleDrawSpectrum,
    member?.callActionMap?.microphoneCamera_audio,
    member?.streams?.microphoneCamera_audio?.enabled,
    member?.streams?.microphoneCamera_audio?.stream,
    member?.user_id,
  ]);

  useEffect(() => {
    onAudioInfo?.({
      hasAudioStream,
      hasVolume,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAudioStream, hasVolume]);

  return (
    <>
      <canvas hidden={!hasAudioStream} width={17} height={12} ref={canvasRef}></canvas>
      <Show when={!hasAudioStream}>
        <CallSpeakerSmallIcon />
      </Show>
    </>
  );
}
